// # Assignments Component for Step 1 Mentorship
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../auth/state';
import DashboardHeader from '../../../components/DashboardHeader';
import DashboardFooter from '../../../components/DashboardFooter';
import { Paper, Typography, Box, Tooltip } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import './assignments1.css';

// # Import Firebase
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import cacheManager from '../../../auth/cache_manager';

// # Import chapter icons
import anatomyIcon from '../../../general/library/step1/step1_icons/anatomy.png';
import biochemistryIcon from '../../../general/library/step1/step1_icons/biochemistry.png';
import bloodIcon from '../../../general/library/step1/step1_icons/blood.png';
import cnsIcon from '../../../general/library/step1/step1_icons/cns.png';
import endocrineIcon from '../../../general/library/step1/step1_icons/endocrine.png';
import eyeIcon from '../../../general/library/step1/step1_icons/eye.png';
import gitIcon from '../../../general/library/step1/step1_icons/git.png';
import heartIcon from '../../../general/library/step1/step1_icons/heart.png';
import immunologyIcon from '../../../general/library/step1/step1_icons/immunology.png';
import infectiousIcon from '../../../general/library/step1/step1_icons/infectious.png';
import kidneyIcon from '../../../general/library/step1/step1_icons/kidney.png';
import lungsIcon from '../../../general/library/step1/step1_icons/lungs.png';
import musculoskeletalIcon from '../../../general/library/step1/step1_icons/musculoskeletal.png';
import pathologyIcon from '../../../general/library/step1/step1_icons/pathology.png';
import pharmaIcon from '../../../general/library/step1/step1_icons/pharma.png';
import psychiatryIcon from '../../../general/library/step1/step1_icons/psychiatry.png';
import publicHealthIcon from '../../../general/library/step1/step1_icons/public_health.png';
import reproductiveIcon from '../../../general/library/step1/step1_icons/reproductive-system.png';
import skinIcon from '../../../general/library/step1/step1_icons/skin.png';
import revisionIcon from '../../../general/library/step1/step1_icons/revision.png';

function Assignments1({ isAdmin }) {
  const navigate = useNavigate();
  const { userProfile, user } = useAuthStore();
  const [targetUserId, setTargetUserId] = useState(null);
  const [isMentor, setIsMentor] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (user && user.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', user.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Get the correct user ID (mentee's ID when mentor is viewing)
  useEffect(() => {
    const menteeId = sessionStorage.getItem('currentViewingMenteeId');
    setTargetUserId(menteeId || user?.uid);
    setIsMentor(!!menteeId);
  }, [user?.uid]);

  // # Define chapters for Step 1
  const chapters = [
    { id: 'Biochemistry', title: 'Biochemistry', icon: biochemistryIcon },
    { id: 'Immunology', title: 'Immunology', icon: immunologyIcon },
    { id: 'Microbiology', title: 'Microbiology', icon: infectiousIcon },
    { id: 'General-Pathology', title: 'General Pathology', icon: pathologyIcon },
    { id: 'Pharmacology', title: 'Pharmacology', icon: pharmaIcon },
    { id: 'Public-Health', title: 'Public Health Sciences', icon: publicHealthIcon },
    { id: 'Anatomy', title: 'Anatomy & Histology', icon: anatomyIcon },
    { id: 'Cardiovascular', title: 'Cardiovascular System', icon: heartIcon },
    { id: 'Endocrine', title: 'Endocrine System', icon: endocrineIcon },
    { id: 'Gastrointestinal', title: 'Gastrointestinal System', icon: gitIcon },
    { id: 'Hematology', title: 'Hematology & Oncology', icon: bloodIcon },
    { id: 'Musculoskeletal', title: 'Musculoskeletal & Connective Tissue', icon: musculoskeletalIcon },
    { id: 'Dermatology', title: 'Dermatology', icon: skinIcon },
    { id: 'Neurology', title: 'Neurology', icon: cnsIcon },
    { id: 'Special-Senses', title: 'Special Senses', icon: eyeIcon },
    { id: 'Psychiatry', title: 'Psychiatry', icon: psychiatryIcon },
    { id: 'Renal', title: 'Renal System', icon: kidneyIcon },
    { id: 'Reproductive', title: 'Reproductive System', icon: reproductiveIcon },
    { id: 'Respiratory', title: 'Respiratory System', icon: lungsIcon },
    { id: 'Rapid-Review', title: 'Rapid Review Series', icon: revisionIcon }
  ];

  return (
    <div className={`assignments1_container ${isDarkMode ? 'assignments1_dark_mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      <div className="assignments1_main">
        <div className="assignments1_content">
          <Typography variant="h4" component="h1" className="assignments1_title">
            Step 1 Assignments {isMentor && <span className="assignments1_mentor_pill">Mentor</span>}
          </Typography>

          <Paper elevation={3} className="assignments1_section">
            <Box className="assignments1_header">
              <Typography variant="h6" component="h2">
                Chapters
              </Typography>
            </Box>

            <div className="assignments1_chapters_grid">
              {chapters.map(chapter => (
                <Tooltip 
                  key={chapter.id}
                  title="Coming Soon"
                  placement="top"
                  arrow
                >
                  <div className="assignments1_chapter_card assignments1_chapter_card_disabled">
                    <div className="assignments1_chapter_icon_wrapper">
                      <img src={chapter.icon} alt={chapter.title} />
                      <div className="assignments1_lock_icon">
                        <LockIcon />
                      </div>
                    </div>
                    <h3>{chapter.title}</h3>
                    <div className="assignments1_coming_soon_badge">Coming Soon</div>
                  </div>
                </Tooltip>
              ))}
            </div>
          </Paper>
        </div>
      </div>
      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
}

export default Assignments1;
